import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '../UserContext';

const POLL_MS = 15000;

const pick = (row, ...keys) => {
    for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null) return row[key];
    }
    return undefined;
};

const normalizeStateDescription = (value) => {
    if (value === 'Closed Scheduled Open') return 'Closed During Regular Hours';
    if (value === 'Open Scheduled Closed') return 'Open Outside Regular Hours';
    return value;
};

// Function JSON is camelCase; UI historically expects PascalCase property names.
const normalizeRow = (row) => {
    const actualOpen = pick(row, 'ActualOpenState', 'actualOpenState');
    const scheduledOpen = pick(row, 'ScheduledOpenState', 'scheduledOpenState');
    return {
        ...row,
        StateDescription: normalizeStateDescription(pick(row, 'StateDescription', 'stateDescription')),
        ActualOpenState: actualOpen === true || actualOpen === 'Open' ? 'Open' : 'Closed',
        ScheduledOpenState: scheduledOpen === true || scheduledOpen === 'Open' ? 'Open' : 'Closed',
        IsDiscrepency: Boolean(pick(row, 'IsDiscrepency', 'isDiscrepency')),
        Timestamp: pick(row, 'Timestamp', 'timestamp', 'eventDateTimeUTC', 'eventDateTime'),
        Lat: pick(row, 'Lat', 'lat'),
        Long: pick(row, 'Long', 'long'),
        PlaceName: pick(row, 'PlaceName', 'placeName'),
        SQLTenantName: pick(row, 'SQLTenantName', 'sqlTenantName'),
        SQLUnitName: pick(row, 'SQLUnitName', 'sqlUnitName'),
        SQLLocationName: pick(row, 'SQLLocationName', 'sqlLocationName'),
        SQLSectionName: pick(row, 'SQLSectionName', 'sqlSectionName'),
        StartTimeString: pick(row, 'StartTimeString', 'startTimeString'),
        EndTimeString: pick(row, 'EndTimeString', 'endTimeString'),
        SectionName: pick(row, 'SectionName', 'sectionName'),
        BuildingName: pick(row, 'BuildingName', 'buildingName'),
    };
};

const rowsSignature = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return '0';
    return rows
        .map((r) => `${r.PlaceName}|${r.SQLTenantName}|${r.StateDescription}|${r.IsDiscrepency}|${r.Timestamp}|${r.ActualOpenState}|${r.ScheduledOpenState}`)
        .join(';');
};

/**
 * Shared poller for getconcessionsgatenow.
 * API returns the full active set (filters are client-side only).
 */
export const useGatesNowData = () => {
    const user = useContext(UserContext);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const signatureRef = useRef('');
    const inFlightRef = useRef(false);
    const apiCallRef = useRef(user?.apiCall);
    apiCallRef.current = user?.apiCall;

    const fetchData = useCallback(async () => {
        if (!apiCallRef.current || inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const res = await apiCallRef.current('getconcessionsgatenow');
            const raw = Array.isArray(res?.data) ? res.data : [];
            const next = raw.map(normalizeRow);
            const nextSig = rowsSignature(next);
            if (nextSig !== signatureRef.current) {
                signatureRef.current = nextSig;
                setRows(next);
            }
            setError(null);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error fetching Gates Now data:', err);
            setError(err);
        } finally {
            inFlightRef.current = false;
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!user?.apiCall) return undefined;
        fetchData();
        const intervalId = setInterval(fetchData, POLL_MS);
        return () => clearInterval(intervalId);
    }, [user?.apiCall, fetchData]);

    const discrepancyRows = useMemo(
        () => rows.filter((r) => r.IsDiscrepency),
        [rows]
    );

    return { rows, discrepancyRows, loading, error, lastUpdated, refresh: fetchData };
};
